// Globals
import { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

// Modules
import { getCategories } from '@/modules/Categories';
import { useCategoriesSelect } from '@/modules/SharedCategories/hooks';
import { CategorySelect } from '@/modules/SharedCategories/components';
import { Resource, usePermissions } from '@/modules/Permissions';

// Store
import { useAppDispatch, useAppState } from '@/store';
import { productsSelector, updateProductFilters } from '../../feature';

// Components
import {
  StyledHeaderBlock,
  StyledContainerTitle,
  StyledContainerWrapper,
  StyledSaveChangesButton,
  StyledForm,
  StyledHintText,
  StyledReadOnlyCategory,
  StyledReadOnlyLabel,
  StyledReadOnlyValue,
} from './ProductDetailsCharacteristicsContainerStyles';
import { showErrorToast, showSuccessToast, useForm, HasUnsavedChangesModal } from '@/components';
import { ProductFiltersBlock } from '../../components';

// Helper
import {
  IProductTabContentProps,
  ProductCharacteristicsFormFieldsNames,
  TProductCharacteristicsFormValues,
} from '../../helpers';
import { useHasUnsavedChangesModal } from '@/hooks';
import { useProductFilters } from '../../hooks';
import { getProductFiltersQueryParams } from '../../helpers';

const initialValues: TProductCharacteristicsFormValues = {
  category: '',
};

const ProductDetailsCharacteristicsContainer = ({
  hasUnsavedChanges,
  pendingTab,
  handleConfirmTabSwitch,
  setHasUnsavedChanges,
  clearUnsavedChanges,
  handleDeclineTabSwitch,
}: IProductTabContentProps) => {
  const { t } = useTranslation(['products']);
  const [form] = useForm<TProductCharacteristicsFormValues>();
  const { product } = useAppState(productsSelector);
  const { id } = useParams();
  const dispatch = useAppDispatch();
  const { canManage } = usePermissions();
  const canManageProduct = canManage(Resource.PRODUCT);

  // needs only for loading of last level categories, if category of current product is on the last level
  const initialCategoryParentId = product?.category?.parent_category?.parent_category
    ? product.category.parent_category.id
    : undefined;

  const { isOpened, onCloseModal, onConfirmTabSwitchAndCloseModal, handleValuesChange } = useHasUnsavedChangesModal({
    hasUnsavedChanges,
    canModalOpen: !!pendingTab,
    handleConfirmTabSwitch,
    setHasUnsavedChanges,
    clearUnsavedChanges,
    handleDeclineTabSwitch,
    isTrackingEnabled: canManageProduct,
  });

  const { categories, autocompleteCategories, onSearch, expandedItems, handleExpandTreeItemData } =
    useCategoriesSelect(initialCategoryParentId);
  const { filters, handleCategoryChange } = useProductFilters(product);

  const handleSubmit = useCallback(async () => {
    try {
      const values = await form.validateFields();
      const queryParams = getProductFiltersQueryParams(values, filters);
      await dispatch(updateProductFilters({ productId: id as string, params: queryParams })).unwrap();
      showSuccessToast({ message: t('data_saved') });
      clearUnsavedChanges();
    } catch (error) {
      showErrorToast({ message: t('product_update_validation_error') });
    }
  }, [filters]);

  const handleSaveAndClose = async () => {
    await handleSubmit();
    onConfirmTabSwitchAndCloseModal();
  };

  // Loading the full categories tree powers the editable category picker and
  // requires `category.view`. View-only roles (e.g. Accountant) lack it, so skip
  // the request and render the characteristics read-only from the product itself.
  useEffect(() => {
    if (canManageProduct) {
      dispatch(getCategories());
    }
  }, [canManageProduct]);

  useEffect(() => {
    if (categories && product?.category?.id) {
      form.setFieldsValue({ category: product?.category.id });
    }
  }, [categories, product]);

  useEffect(() => {
    const currentValues = form.getFieldsValue();
    const resetValues: Record<string, undefined> = {};
    Object.keys(currentValues).forEach((key) => {
      if (key !== ProductCharacteristicsFormFieldsNames.CATEGORY) {
        resetValues[key] = undefined;
      }
    });
    form.setFieldsValue(resetValues);

    if (filters.length > 0) {
      const initialFilters: Record<string, string | string[]> = {};
      filters.forEach((filter) => {
        const selectedValues = filter.values.filter((value) => value.selected).map((value) => value.id);
        initialFilters[filter.id] = selectedValues.length > 1 ? selectedValues : selectedValues[0];
      });
      form.setFieldsValue(initialFilters);
    }
  }, [filters]);

  return (
    <StyledContainerWrapper>
      <StyledHeaderBlock>
        <StyledContainerTitle>{t('characteristics_container_title')}</StyledContainerTitle>
        {canManageProduct && (
          <StyledSaveChangesButton htmlType="submit" onClick={handleSubmit}>
            {t('product_details_save_changes_button')}
          </StyledSaveChangesButton>
        )}
      </StyledHeaderBlock>
      <StyledForm initialValues={initialValues} onFinish={handleSubmit} form={form} onValuesChange={handleValuesChange}>
        {canManageProduct ? (
          <>
            <CategorySelect
              categories={categories}
              autocompleteCategories={autocompleteCategories}
              expandedItems={expandedItems}
              handleExpandTreeItemData={handleExpandTreeItemData}
              onSearch={onSearch}
              formItemName={ProductCharacteristicsFormFieldsNames.CATEGORY}
              handleCategoryChange={handleCategoryChange}
            />
            <StyledHintText>{t('product_category_hint')}</StyledHintText>
          </>
        ) : (
          <StyledReadOnlyCategory>
            <StyledReadOnlyLabel>{t('products_list_category_title')}</StyledReadOnlyLabel>
            <StyledReadOnlyValue>{product?.category?.name ?? '—'}</StyledReadOnlyValue>
          </StyledReadOnlyCategory>
        )}
        <ProductFiltersBlock filters={filters} />
      </StyledForm>
      <HasUnsavedChangesModal
        isOpened={isOpened}
        handleCloseModal={onCloseModal}
        handleSaveAndClose={handleSaveAndClose}
        handleExitAndClose={onConfirmTabSwitchAndCloseModal}
      />
    </StyledContainerWrapper>
  );
};

export default ProductDetailsCharacteristicsContainer;
